import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import AccountsPage from './pages/AccountsPage';
import AccountDetailPage from './pages/AccountDetailPage';
import ImportPage from './pages/ImportPage';
import ExportPage from './pages/ExportPage';
import ProfilePage from './pages/ProfilePage';
import NBNOutagesPage from './pages/NBNOutagesPage';
import MobileOutagesPage from './pages/MobileOutagesPage';
import VoiceOutagesPage from './pages/VoiceOutagesPage';
import WebServicesOutagesPage from './pages/WebServicesOutagesPage';
import SettingsPage from './pages/SettingsPage';
import MyNotificationsPage from './pages/MyNotificationsPage';
import { CarrierStatusPage, SystemLogsPage, ChangelogPage } from './pages/system';
import OrderPage from './pages/OrderPage';
import OrderEmbedPage from './pages/OrderEmbedPage';
import OrderReviewPage from './pages/OrderReviewPage';
import OrdersPage from './pages/admin/OrdersPage';
import OrderDetailPage from './pages/admin/OrderDetailPage';
import ProductsPage from './pages/admin/ProductsPage';
import HardwarePage from './pages/admin/HardwarePage';
import AddOnsPage from './pages/admin/AddOnsPage';
import CategoriesPage from './pages/admin/CategoriesPage';
import CouponsPage from './pages/admin/CouponsPage';
import SynergyImportPage from './pages/admin/SynergyImportPage';
import ServicesReportPage from './pages/reports/ServicesReportPage';
import ServicesMapPage from './pages/reports/ServicesMapPage';
import InternetServicesReportPage from './pages/reports/InternetServicesReportPage';
import DomainNamesReportPage from './pages/reports/DomainNamesReportPage';
import InternetFinancialReportPage from './pages/reports/InternetFinancialReportPage';
import MobileFinancialReportPage from './pages/reports/MobileFinancialReportPage';
import AdminRoute from './components/AdminRoute';
import CasesPage from './pages/CasesPage';
import CaseDetailPage from './pages/CaseDetailPage';
import SupportDeskPage from './pages/SupportDeskPage';
import SupportDeskDetailPage from './pages/SupportDeskDetailPage';
import TasksPage from './pages/TasksPageV2';
import TaskDetailPage from './pages/TaskDetailPageV2';
import SupaServiceQualificationPage from './pages/tools/SupaServiceQualificationPage';
import RadiusLookupPage from './pages/tools/RadiusLookupPage';
import GoogleReviewsPage from './pages/marketing/GoogleReviewsPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import KnowledgeBaseArticlePage from './pages/KnowledgeBaseArticlePage';
import KnowledgeBaseEditPage from './pages/KnowledgeBaseEditPage';
import { SONIC_APP_FONT_FAMILY } from './theme/appFontFamily';
import '@mui/x-data-grid/themeAugmentation';

// Sonic customer-comms theme: #0d1f3c navy, #3b9eff blue, #f0f2f5 background (matches email templates)
const theme = createTheme({
  palette: {
    primary: {
      main: '#0d1f3c', // Navy header/footer from customer emails
    },
    secondary: {
      main: '#3b9eff', // Link/CTA blue from customer emails
    },
    success: {
      main: '#1a4d3a',
      light: '#7ecfaa',
    },
    info: {
      main: '#0284c7',
      light: '#7fa8d4',
    },
    background: {
      default: '#f0f2f5', // Outer wrapper from email
      paper: '#ffffff',
    },
    text: {
      primary: '#0d1f3c',
      secondary: '#4a5568',
      disabled: '#8a93a0',
    },
  },
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: SONIC_APP_FONT_FAMILY,
    h1: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.25, color: '#0d1f3c' },
    h2: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.3, color: '#0d1f3c' },
    h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.35, color: '#0d1f3c' },
    h4: { fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.35, color: '#0d1f3c' },
    h5: { fontSize: '1.0625rem', fontWeight: 600, lineHeight: 1.35, color: '#0d1f3c' },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.35, color: '#0d1f3c' },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.4, color: '#4a5568' },
    subtitle2: { fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.45, color: '#4a5568' },
    body1: { fontSize: '0.875rem', lineHeight: 1.55, color: '#4a5568' },
    body2: { fontSize: '0.8125rem', lineHeight: 1.45, color: '#4a5568' },
    caption: { fontSize: '0.75rem', lineHeight: 1.4, color: '#4a5568' },
    button: { fontSize: '0.8125rem', textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.06)',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#f0f2f5',
          fontFamily: SONIC_APP_FONT_FAMILY,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          fontFamily: SONIC_APP_FONT_FAMILY,
          backgroundColor: '#0d1f3c',
          color: '#ffffff',
          '& .MuiListItemIcon-root': { color: '#a8c8f0' },
          '& .MuiListItemText-primary': { color: '#ffffff' },
          '& .MuiTypography-root': { color: 'inherit' },
          '& .MuiListItemButton-root': {
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
            '&.Mui-selected': {
              backgroundColor: 'rgba(59, 158, 255, 0.2)',
              '&:hover': { backgroundColor: 'rgba(59, 158, 255, 0.25)' },
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          fontFamily: SONIC_APP_FONT_FAMILY,
          backgroundColor: '#0d1f3c',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          fontFamily: 'inherit',
        },
        head: {
          fontSize: '0.75rem',
          fontWeight: 600,
          letterSpacing: '0.02em',
          backgroundColor: '#1e3a5c',
          color: '#ffffff',
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        columnHeaders: {
          backgroundColor: '#1e3a5c',
        },
        columnHeader: {
          backgroundColor: '#1e3a5c',
          color: '#ffffff',
          fontSize: '0.75rem',
          fontWeight: 600,
          letterSpacing: '0.02em',
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
          },
          '& .MuiDataGrid-sortIcon': {
            color: '#ffffff',
          },
          '& .MuiDataGrid-menuIcon .MuiSvgIcon-root': {
            color: '#ffffff',
          },
        },
        columnSeparator: {
          color: 'rgba(255,255,255,0.2)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardWarning: {
          borderLeft: '4px solid #ed6c02',
        },
        standardInfo: {
          borderLeft: '4px solid #3b9eff',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/order/embed" element={<OrderEmbedPage />} />
            <Route path="/order" element={<OrderPage />} />
            <Route path="/order-review/:token" element={<OrderReviewPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="accounts" element={<AccountsPage />} />
              <Route path="accounts/:id" element={<AccountDetailPage />} />
              <Route path="import" element={<ImportPage />} />
              <Route path="export" element={<ExportPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="my-notifications" element={<MyNotificationsPage />} />
              <Route path="internet-outages" element={<NBNOutagesPage />} />
              <Route path="nbn-outages" element={<NBNOutagesPage />} />
              <Route path="mobile-outages" element={<MobileOutagesPage />} />
              <Route path="voice-outages" element={<VoiceOutagesPage />} />
              <Route path="web-services-outages" element={<WebServicesOutagesPage />} />
              <Route path="system/feed-status" element={<CarrierStatusPage />} />
              <Route
                path="system/carriers"
                element={<Navigate to="/system/feed-status" replace />}
              />
              <Route path="system/logs" element={<SystemLogsPage />} />
              <Route path="system/changelog" element={<ChangelogPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="admin/orders" element={<OrdersPage />} />
              <Route path="admin/orders/:id" element={<OrderDetailPage />} />
              <Route path="admin/products" element={<ProductsPage />} />
              <Route path="admin/hardware" element={<HardwarePage />} />
              <Route path="admin/addons" element={<AddOnsPage />} />
              <Route path="admin/categories" element={<CategoriesPage />} />
              <Route path="admin/coupons" element={<CouponsPage />} />
              <Route path="admin/domains/import" element={<SynergyImportPage />} />
              <Route path="reports/services" element={<ServicesReportPage />} />
              <Route path="reports/services-map" element={<ServicesMapPage />} />
              <Route path="reports/internet-services" element={<InternetServicesReportPage />} />
              <Route path="reports/domain-names" element={<DomainNamesReportPage />} />
              <Route path="reports/internet-financial" element={<AdminRoute><InternetFinancialReportPage /></AdminRoute>} />
              <Route path="reports/mobile-financial" element={<AdminRoute><MobileFinancialReportPage /></AdminRoute>} />
              <Route path="crm/cases" element={<CasesPage />} />
              <Route path="crm/tasks" element={<TasksPage />} />
              <Route path="crm/tasks/:id" element={<TaskDetailPage />} />
              <Route path="crm/support-desk" element={<SupportDeskPage />} />
              <Route path="crm/support-desk/:id" element={<SupportDeskDetailPage />} />
              <Route path="crm/cases/:id" element={<CaseDetailPage />} />
              <Route path="tools/service-qualification" element={<SupaServiceQualificationPage />} />
              <Route path="tools/radius-lookup" element={<RadiusLookupPage />} />
              <Route path="marketing/google-reviews" element={<GoogleReviewsPage />} />
              <Route path="knowledge-base" element={<KnowledgeBasePage />} />
              <Route path="knowledge-base/new" element={<KnowledgeBaseEditPage />} />
              <Route path="knowledge-base/:id" element={<KnowledgeBaseArticlePage />} />
              <Route path="knowledge-base/:id/edit" element={<KnowledgeBaseEditPage />} />
            </Route>
          </Routes>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
